import { Form, Link, useNavigation } from 'react-router-dom';
import { TaskCategory, TasksApplicabilityOverrideType } from '@mosey/api-types';
import { BaseCard } from '@mosey/components/layout/BaseCard';
import { TruncatedList } from '@mosey/components/layout/TruncatedList';
import { ExtLinkIcon } from '@mosey/components/Icons';
import { ZeroState } from '@mosey/components/layout/ZeroState';
import {
  useTasksOverviewData,
  useTasksOverviewLocation,
  useTasksOverviewUrl,
} from '../../utils/hooks';
import { useUser } from '../../../../hooks/useUser';
import { ModuleHeading } from './ModuleHeading';
import { ResolverType } from '../../utils/types';
import { generateResolverTasksBrowserUrl } from '../../utils';
import handbookIcon from '../../../../assets/tasks-framework/tasks-overview-employee-resources-empty-handbook.svg';
import employeeRegistration from '../../../../assets/tasks-framework/tasks-overview-sidebar-employee-registration.svg';

export const EmployeeResourcesCollection = () => {
  const navigation = useNavigation();
  const {
    legal_entity: {
      notice_url: noticeUrl,
      regions,
      is_handbook_enabled: isHandbookEnabled,
    },
  } = useUser();
  const location = useTasksOverviewLocation();
  const tasksOverviewUrl = useTasksOverviewUrl();
  const { handbookStats, availableOverrides = [] } = useTasksOverviewData();
  let hasEmployees = false;
  if (location) {
    const legalEntityRegion = regions.find(
      (region) => region.code.toLowerCase() === location.code.toLowerCase(),
    );

    if (legalEntityRegion) {
      hasEmployees = legalEntityRegion.has_employees === true;
    }
  } else {
    for (let i = 0; i < regions.length; i++) {
      const region = regions[i];

      if (region.has_employees) {
        hasEmployees = true;
        break;
      }
    }
  }

  const hasPayrollRegistrationOverride =
    location &&
    availableOverrides.includes(
      TasksApplicabilityOverrideType.payroll_registration,
    );

  let percentSigned = null;
  let isHandbookPublished = false;

  if (handbookStats !== null) {
    const {
      acknowledgements: { signed, pending, outdated },
      handbooks: { published },
    } = handbookStats;
    const total = signed + pending + outdated;
    isHandbookPublished = published > 0;

    if (total > 0) {
      percentSigned = Math.round((signed / total) * 100);
    }
  }

  return (
    <TruncatedList
      title={<ModuleHeading>Employee Resources</ModuleHeading>}
      isStacked
    >
      {hasPayrollRegistrationOverride && (
        <li className="group/card-component">
          <Form method="POST" className="text-sm" action={tasksOverviewUrl}>
            <input type="hidden" name="location_id" value={location.code} />
            <input
              type="hidden"
              name="override_type"
              value={TasksApplicabilityOverrideType.payroll_registration}
            />
            <input
              type="hidden"
              name="redirect_url"
              value={generateResolverTasksBrowserUrl({
                resolverType: ResolverType.Todo,
                locationId: location.code,
                category: TaskCategory.payroll,
              })}
            />

            <ZeroState
              variant="actionable"
              graphic={<img src={employeeRegistration} alt="" />}
              title="Start payroll registration"
              description="Get ready to onboard a new hire."
              as="button"
              type="submit"
              name="intent"
              value="add-applicability-override"
              disabled={navigation.state === 'submitting'}
            />
          </Form>
        </li>
      )}

      {!isHandbookEnabled && hasEmployees && (
        <li className="group/card-component">
          <ZeroState
            variant="actionable"
            graphic={<img src={handbookIcon} alt="" />}
            title="Launch your multi-state employee handbook"
            description="Make a compliant handbook for all 50 states, in minutes."
            as={Link}
            to="/handbook"
          />
        </li>
      )}

      {isHandbookEnabled && hasEmployees && (
        <li className="group/card-component">
          <BaseCard
            size="medium"
            variant="nav"
            data-testid="employee-resources-handbook-card"
            title="Handbook"
            target={isHandbookPublished ? '_blank' : undefined}
            to={
              isHandbookPublished
                ? `/handbook-preview${location ? `?region=${location.code}` : ''}`
                : '/handbook'
            }
            rightIcon={
              isHandbookPublished && (
                <ExtLinkIcon aria-label="– opens in new tab" />
              )
            }
            footer={
              isHandbookPublished
                ? `Signed by ${percentSigned}% of employees`
                : 'Continue setup'
            }
          />
        </li>
      )}

      {noticeUrl && hasEmployees && (
        <li className="group/card-component">
          <BaseCard
            size="medium"
            variant="nav"
            title="Labor Notices & Posters"
            rightIcon={<ExtLinkIcon aria-label="– opens in new tab" />}
            target="_blank"
            to={`${noticeUrl}${location ? `?region=${location.name}` : ''}`}
          />
        </li>
      )}
    </TruncatedList>
  );
};
